<template>
  <div>
    <div class="pa-2 mb-2">
      <autocomplete
        v-model="selectedCompany"
        :items="companies"
        :label="$t('select_company')"
        :loading="isLoadingCompanies"
        hide-details
        clearable
        @change="loadData"
      />
    </div>

    <korisnici-table :data="data" :loading="isLoading" :search-query="searchQuery" />
  </div>
</template>

<script>
import KorisniciTable from "@/components/guest/KorisniciTable.vue";
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "KorisniciTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    KorisniciTable,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
  },
  data() {
    return {
      data: [],
      isLoading: false,
      selectedCompany: null,
      companies: [],
      isLoadingCompanies: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("id"), value: "id", sortable: false },
        { text: this.$t("name_surname"), value: "nameAndEmail", sortable: false },
        {
          text: this.$t("ordered"),
          value: "order_count",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("taken"),
          value: "order_payed",
          sortable: false,
          align: "center",
        },
      ];
    },
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printKorisniciTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportKorisniciExcel();
    },
    dateRange: {
      handler() {
        this.loadCompanies();
      },
      immediate: true,
    },
  },
  methods: {
    async loadCompanies() {
      if (!this.restaurantId || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoadingCompanies = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        const response = await reportService.getRestaurantGuestCompanies(
          this.restaurantId,
          params
        );

        // Extract keys from response object as company names
        this.companies = Object.keys(response.data || {});

        // Auto-select first company if available
        if (this.companies.length > 0 && !this.selectedCompany) {
          this.selectedCompany = this.companies[0];
          this.loadData();
        }
      } catch (error) {
        console.error("Error loading companies:", error);
        this.companies = [];
      } finally {
        this.isLoadingCompanies = false;
      }
    },
    async loadData() {
      if (
        !this.restaurantId ||
        !this.dateRange ||
        this.dateRange.length !== 2 ||
        !this.selectedCompany
      ) {
        this.data = [];
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
        };

        const response = await reportService.getCompanyGuestKorisnici(
          this.selectedCompany,
          params
        );

        // Extract users array from response
        this.data = response.data?.users || [];
      } catch (error) {
        console.error("Error loading korisnici data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    printKorisniciTable() {
      const tableItems = this.data.map((item, index) => {
        const name = this.toLatin(item.name);
        const lastname = this.toLatin(item.lastname);
        const email =
          item.email || (item.card_code ? `${item.card_code}@sportek` : "N/A");

        return {
          num: index + 1,
          id: item.id,
          nameAndEmail: `<div style="line-height: 1.4;">
            <div style="font-size: 1.1em; margin-bottom: 4px; font-weight: 500;">${name} ${lastname}</div>
            <div style="font-size: 0.9em; color: #666;">${email}</div>
          </div>`,
          order_count: item.order_count || 0,
          order_payed: item.order_payed || 0,
        };
      });

      this.printDocument({
        pageTitle: this.$t("korisnici"),
        title: this.selectedCompany || this.$t("korisnici"),
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}`,
        tables: [
          {
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
    exportKorisniciExcel() {
      const tableItems = this.data.map((item, index) => {
        const name = this.toLatin(item.name);
        const lastname = this.toLatin(item.lastname);
        const email =
          item.email || (item.card_code ? `${item.card_code}@sportek` : "N/A");

        return {
          num: index + 1,
          id: item.id,
          nameAndEmail: `${name} ${lastname}\n${email}`,
          order_count: item.order_count || 0,
          order_payed: item.order_payed || 0,
        };
      });

      this.downloadExcel({
        fileName: `korisnici_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t("korisnici"),
            title: this.selectedCompany || this.$t("korisnici"),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )}`,
            headers: this.headers.map((h) => ({
              text: h.text,
              value: h.value,
              align: h.align,
            })),
            items: tableItems,
          },
        ],
      });
    },
  },
};
</script>
